import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Users, Search, Phone, Mail, MapPin, DollarSign, Eye, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Customers = ({ isAdmin = false }) => {
  const [customers, setCustomers] = useState([]);
  const [listings, setListings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [viewingCustomer, setViewingCustomer] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    budget_min: '',
    budget_max: '',
    preferred_locations: [],
    property_preferences: '',
    status: 'active',
    notes: '',
    interested_listings: [],
    assigned_to: ''
  });
  const [newLocation, setNewLocation] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [customersRes, listingsRes] = await Promise.all([
        axios.get(`${API}/customers/`),
        axios.get(`${API}/listings/`)
      ]);
      setCustomers(customersRes.data);
      setListings(listingsRes.data);
      
      if (isAdmin) {
        const employeesRes = await axios.get(`${API}/users/employees`);
        setEmployees(employeesRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const data = {
      ...formData,
      budget_min: formData.budget_min ? parseFloat(formData.budget_min) : null,
      budget_max: formData.budget_max ? parseFloat(formData.budget_max) : null,
      assigned_to: formData.assigned_to || null
    };

    try {
      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.id}`, data);
        toast.success('Customer updated successfully');
      } else {
        await axios.post(`${API}/customers/`, data);
        toast.success('Customer added successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save customer');
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      first_name: customer.first_name,
      last_name: customer.last_name,
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      city: customer.city || '',
      state: customer.state || '',
      zip_code: customer.zip_code || '',
      budget_min: customer.budget_min?.toString() || '',
      budget_max: customer.budget_max?.toString() || '',
      preferred_locations: customer.preferred_locations || [],
      property_preferences: customer.property_preferences || '',
      status: customer.status,
      notes: customer.notes || '',
      interested_listings: customer.interested_listings || [],
      assigned_to: customer.assigned_to || ''
    });
    setDialogOpen(true);
  };

  const handleView = (customer) => {
    setViewingCustomer(customer);
    setViewDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    
    try {
      await axios.delete(`${API}/customers/${id}`);
      toast.success('Customer deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete customer');
    }
  };

  const resetForm = () => {
    setEditingCustomer(null);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      budget_min: '',
      budget_max: '',
      preferred_locations: [],
      property_preferences: '',
      status: 'active',
      notes: '',
      interested_listings: [],
      assigned_to: ''
    });
    setNewLocation('');
  };

  const addLocation = () => {
    if (newLocation && !formData.preferred_locations.includes(newLocation)) {
      setFormData({ ...formData, preferred_locations: [...formData.preferred_locations, newLocation] });
      setNewLocation('');
    }
  };

  const removeLocation = (loc) => {
    setFormData({ ...formData, preferred_locations: formData.preferred_locations.filter(l => l !== loc) });
  };

  const toggleInterestedListing = (listingId) => {
    if (formData.interested_listings.includes(listingId)) {
      setFormData({ 
        ...formData, 
        interested_listings: formData.interested_listings.filter(id => id !== listingId) 
      });
    } else {
      setFormData({ 
        ...formData, 
        interested_listings: [...formData.interested_listings, listingId] 
      });
    }
  };

  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  const filteredCustomers = customers.filter(customer => {
    const fullName = `${customer.first_name} ${customer.last_name}`.toLowerCase();
    const matchesSearch = fullName.includes(searchTerm.toLowerCase()) ||
                         (customer.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                         (customer.phone || '').includes(searchTerm);
    const matchesStatus = statusFilter === 'all' || customer.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: customers.length,
    active: customers.filter(c => c.status === 'active').length,
    inactive: customers.filter(c => c.status === 'inactive').length,
    closed: customers.filter(c => c.status === 'closed').length
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-foreground">
            Customer Management
          </h1>
          <p className="font-body text-muted-foreground mt-1">
            Manage your customer leads and contacts
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="rounded-none font-heading font-bold uppercase tracking-wide bg-brand-gold text-brand-brown hover:bg-brand-gold/90" data-testid="add-customer-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold">
                {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    First Name *
                  </Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                    className="rounded-none border-2"
                    data-testid="customer-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Last Name *
                  </Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                    className="rounded-none border-2"
                    data-testid="customer-last-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Email
                  </Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="rounded-none border-2"
                    data-testid="customer-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Phone
                  </Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="rounded-none border-2"
                    data-testid="customer-phone"
                  />
                </div>
                <div className="md:col-span-2 space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Address
                  </Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="rounded-none border-2"
                    data-testid="customer-address"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    City
                  </Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="rounded-none border-2"
                    data-testid="customer-city"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    State
                  </Label>
                  <Input
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="rounded-none border-2"
                    data-testid="customer-state"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Budget Min
                  </Label>
                  <Input
                    type="number"
                    value={formData.budget_min}
                    onChange={(e) => setFormData({ ...formData, budget_min: e.target.value })}
                    placeholder="$0"
                    className="rounded-none border-2"
                    data-testid="customer-budget-min"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Budget Max
                  </Label>
                  <Input
                    type="number"
                    value={formData.budget_max}
                    onChange={(e) => setFormData({ ...formData, budget_max: e.target.value })}
                    placeholder="No limit"
                    className="rounded-none border-2"
                    data-testid="customer-budget-max"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Status
                  </Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                    <SelectTrigger className="rounded-none border-2" data-testid="customer-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {isAdmin && (
                  <div className="space-y-2">
                    <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                      Assign To Employee
                    </Label>
                    <Select value={formData.assigned_to || "unassigned"} onValueChange={(value) => setFormData({ ...formData, assigned_to: value === "unassigned" ? "" : value })}>
                      <SelectTrigger className="rounded-none border-2" data-testid="customer-assignee">
                        <SelectValue placeholder="Select employee..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {employees.map((emp) => (
                          <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Preferred Locations */}
                <div className="md:col-span-2 space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Preferred Locations
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add location..."
                      value={newLocation}
                      onChange={(e) => setNewLocation(e.target.value)}
                      className="rounded-none border-2"
                      data-testid="location-input"
                    />
                    <Button type="button" onClick={addLocation} variant="outline" className="rounded-none border-2" data-testid="add-location-btn">
                      Add
                    </Button>
                  </div>
                  {formData.preferred_locations.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.preferred_locations.map((loc) => (
                        <Badge key={loc} variant="secondary" className="rounded-none flex items-center gap-1">
                          {loc}
                          <button type="button" onClick={() => removeLocation(loc)} className="hover:text-destructive">
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <div className="md:col-span-2 space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Property Preferences
                  </Label>
                  <Input
                    value={formData.property_preferences}
                    onChange={(e) => setFormData({ ...formData, property_preferences: e.target.value })}
                    placeholder="e.g., 3 bed, 2 bath, garage, pool..."
                    className="rounded-none border-2"
                    data-testid="customer-preferences"
                  />
                </div>

                <div className="md:col-span-2 space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Notes
                  </Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="rounded-none border-2"
                    data-testid="customer-notes"
                  />
                </div>

                {/* Interested Listings */}
                {listings.length > 0 && (
                  <div className="md:col-span-2 space-y-2">
                    <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                      Interested In Properties
                    </Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto border-2 p-2">
                      {listings.filter(l => l.status === 'available').map((listing) => (
                        <div 
                          key={listing.id}
                          onClick={() => toggleInterestedListing(listing.id)}
                          className={`p-2 border cursor-pointer transition-colors ${
                            formData.interested_listings.includes(listing.id) 
                              ? 'border-brand-gold bg-brand-gold/10' 
                              : 'border-border hover:border-brand-gold/50'
                          }`}
                        >
                          <p className="font-heading text-sm font-semibold truncate">{listing.title}</p>
                          <p className="font-body text-xs text-muted-foreground">{formatPrice(listing.price)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-4 pt-4">
                <Button type="submit" className="rounded-none font-heading font-bold uppercase tracking-wide bg-brand-gold text-brand-brown hover:bg-brand-gold/90" data-testid="save-customer-btn">
                  {editingCustomer ? 'Update Customer' : 'Add Customer'}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="rounded-none border-2">
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="rounded-none border-border">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <Users className="w-4 h-4" />
              Total Customers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-heading text-3xl font-bold" data-testid="stat-total">
              {stats.total}
            </p>
          </CardContent>
        </Card>
        <Card className="rounded-none border-border">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-[10px] uppercase tracking-widest text-muted-foreground">
              Active
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-heading text-3xl font-bold text-green-600" data-testid="stat-active">
              {stats.active}
            </p>
          </CardContent>
        </Card>
        <Card className="rounded-none border-border">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-[10px] uppercase tracking-widest text-muted-foreground">
              Inactive
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-heading text-3xl font-bold text-yellow-600" data-testid="stat-inactive">
              {stats.inactive}
            </p>
          </CardContent>
        </Card>
        <Card className="rounded-none border-border">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-[10px] uppercase tracking-widest text-muted-foreground">
              Closed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-heading text-3xl font-bold text-brand-gold" data-testid="stat-closed">
              {stats.closed}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="rounded-none border-2 pl-10"
            data-testid="search-customers"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px] rounded-none border-2" data-testid="filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/50">
              <TableHead className="font-heading text-xs uppercase tracking-widest">Customer</TableHead>
              <TableHead className="font-heading text-xs uppercase tracking-widest">Contact</TableHead>
              <TableHead className="font-heading text-xs uppercase tracking-widest">Budget</TableHead>
              <TableHead className="font-heading text-xs uppercase tracking-widest">Status</TableHead>
              <TableHead className="font-heading text-xs uppercase tracking-widest text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12">
                  <div className="w-8 h-8 border-4 border-brand-gold border-t-transparent rounded-full animate-spin mx-auto"></div>
                </TableCell>
              </TableRow>
            ) : filteredCustomers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12">
                  <Users className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                  <p className="font-body text-muted-foreground">No customers found</p>
                </TableCell>
              </TableRow>
            ) : (
              filteredCustomers.map((customer) => (
                <TableRow key={customer.id} className="hover:bg-secondary/30" data-testid={`customer-row-${customer.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-brand-brown text-white flex items-center justify-center font-heading font-bold">
                        {customer.first_name.charAt(0)}{customer.last_name.charAt(0)}
                      </div>
                      <div>
                        <p className="font-heading font-semibold text-sm">{customer.first_name} {customer.last_name}</p>
                        {customer.city && (
                          <p className="font-body text-xs text-muted-foreground flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {customer.city}
                          </p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {customer.email && (
                        <p className="font-body text-xs flex items-center gap-1">
                          <Mail className="w-3 h-3 text-muted-foreground" /> {customer.email}
                        </p>
                      )}
                      {customer.phone && (
                        <p className="font-body text-xs flex items-center gap-1">
                          <Phone className="w-3 h-3 text-muted-foreground" /> {customer.phone}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm">
                      <DollarSign className="w-3 h-3 text-muted-foreground" />
                      <span className="font-body">
                        {customer.budget_min || customer.budget_max 
                          ? `${formatPrice(customer.budget_min)} - ${formatPrice(customer.budget_max)}`
                          : 'Not specified'
                        }
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={`rounded-none font-heading text-[10px] uppercase tracking-widest ${
                      customer.status === 'active' 
                        ? 'bg-green-600 text-white' 
                        : customer.status === 'inactive'
                        ? 'bg-yellow-500 text-white'
                        : 'bg-brand-gold text-brand-brown'
                    }`}>
                      {customer.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleView(customer)} className="p-2" data-testid={`view-customer-${customer.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(customer)} className="p-2" data-testid={`edit-customer-${customer.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(customer.id)} className="p-2 text-destructive hover:text-destructive" data-testid={`delete-customer-${customer.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* View Customer Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">
              Customer Details
            </DialogTitle>
          </DialogHeader>
          {viewingCustomer && (
            <div className="space-y-6 mt-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-brand-brown text-white flex items-center justify-center font-heading font-bold text-xl">
                  {viewingCustomer.first_name.charAt(0)}{viewingCustomer.last_name.charAt(0)}
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold">
                    {viewingCustomer.first_name} {viewingCustomer.last_name}
                  </h3>
                  <Badge className={`rounded-none font-heading text-[10px] uppercase tracking-widest ${
                    viewingCustomer.status === 'active' 
                      ? 'bg-green-600 text-white' 
                      : viewingCustomer.status === 'inactive'
                      ? 'bg-yellow-500 text-white'
                      : 'bg-brand-gold text-brand-brown'
                  }`}>
                    {viewingCustomer.status}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {viewingCustomer.email && (
                  <div>
                    <p className="font-heading text-[10px] uppercase tracking-widest text-muted-foreground">Email</p>
                    <p className="font-body text-sm">{viewingCustomer.email}</p>
                  </div>
                )}
                {viewingCustomer.phone && (
                  <div>
                    <p className="font-heading text-[10px] uppercase tracking-widest text-muted-foreground">Phone</p>
                    <p className="font-body text-sm">{viewingCustomer.phone}</p>
                  </div>
                )}
                {(viewingCustomer.budget_min || viewingCustomer.budget_max) && (
                  <div className="col-span-2">
                    <p className="font-heading text-[10px] uppercase tracking-widest text-muted-foreground">Budget</p>
                    <p className="font-body text-sm font-semibold text-brand-gold">
                      {formatPrice(viewingCustomer.budget_min)} - {formatPrice(viewingCustomer.budget_max)}
                    </p>
                  </div>
                )}
                {viewingCustomer.preferred_locations?.length > 0 && (
                  <div className="col-span-2">
                    <p className="font-heading text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Preferred Locations</p>
                    <div className="flex flex-wrap gap-1">
                      {viewingCustomer.preferred_locations.map((loc) => (
                        <Badge key={loc} variant="secondary" className="rounded-none">{loc}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {viewingCustomer.property_preferences && (
                  <div className="col-span-2">
                    <p className="font-heading text-[10px] uppercase tracking-widest text-muted-foreground">Property Preferences</p>
                    <p className="font-body text-sm">{viewingCustomer.property_preferences}</p>
                  </div>
                )}
                {viewingCustomer.notes && (
                  <div className="col-span-2">
                    <p className="font-heading text-[10px] uppercase tracking-widest text-muted-foreground">Notes</p>
                    <p className="font-body text-sm">{viewingCustomer.notes}</p>
                  </div>
                )}
              </div>

              <div className="pt-4 border-t">
                <Button onClick={() => { setViewDialogOpen(false); handleEdit(viewingCustomer); }} className="rounded-none font-heading uppercase tracking-wide bg-brand-gold text-brand-brown hover:bg-brand-gold/90">
                  Edit Customer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Customers;
